// Project colour palette shared by the server and the client bundle (architecture sprint-02
// §3.4). Dependency-free like calendar.ts (tests/unit/shared-purity.test.ts). Treat the list as
// fixed data: stored project colours must stay members of it.

export interface PaletteColor {
  /** '#RRGGBB', upper-case. */
  hex: string;
  name: string;
}

export const PROJECT_PALETTE: readonly PaletteColor[] = [
  // 1–12: the original "Time" app presets, original order (theme-palette-original.md).
  { hex: '#007ACC', name: 'Blue' },
  { hex: '#F59E0B', name: 'Amber' },
  { hex: '#EF4444', name: 'Red' },
  { hex: '#8B5CF6', name: 'Violet' },
  { hex: '#3B82F6', name: 'Royal blue' },
  { hex: '#10B981', name: 'Emerald' },
  { hex: '#F97316', name: 'Orange' },
  { hex: '#EC4899', name: 'Pink' },
  { hex: '#06B6D4', name: 'Cyan' },
  { hex: '#6366F1', name: 'Indigo' },
  { hex: '#84CC16', name: 'Lime' },
  { hex: '#94A3B8', name: 'Slate' },
  // 13–24: added in sprint 2. Tailwind v3 400–700 chromatic shades with contrast >= 3:1 on the
  // dark card #252526 and >= 2.5:1 on white, picked greedily to maximise the minimum CIEDE2000
  // distance to every colour already in the palette and to the "No project" neutral.
  { hex: '#4D7C0F', name: 'Olive' },
  { hex: '#A16207', name: 'Ochre' },
  { hex: '#0D9488', name: 'Teal' },
  { hex: '#C084FC', name: 'Lavender' },
  { hex: '#FB7185', name: 'Rose' },
  { hex: '#C026D3', name: 'Magenta' },
  { hex: '#0EA5E9', name: 'Sky' },
  { hex: '#65A30D', name: 'Leaf' },
  { hex: '#0891B2', name: 'Ocean' },
  { hex: '#15803D', name: 'Forest' },
  { hex: '#059669', name: 'Jade' },
  { hex: '#818CF8', name: 'Periwinkle' },
];

/** "No project" neutral. Deliberately NOT a palette member, so no project can look like it. */
export const NO_PROJECT_COLOR = '#6B7280';
export const NO_PROJECT_LABEL = 'No project';

export function normalizeHex(hex: string): string {
  return hex.trim().toUpperCase();
}

export function isPaletteColor(hex: string): boolean {
  const normalized = normalizeHex(hex);
  return PROJECT_PALETTE.some((c) => c.hex === normalized);
}

export function paletteColorName(hex: string): string | null {
  const normalized = normalizeHex(hex);
  return PROJECT_PALETTE.find((c) => c.hex === normalized)?.name ?? null;
}

/** First palette colour not used by an active project; if all 24 are used, the first colour. */
export function firstUnusedColor(activeProjectColors: readonly string[]): string {
  const used = new Set(activeProjectColors.map(normalizeHex));
  const free = PROJECT_PALETTE.find((c) => !used.has(c.hex));
  return (free ?? PROJECT_PALETTE[0] ?? { hex: '#007ACC' }).hex;
}
